import { MIN_WIDTH, ACTIONS_WIDTH } from '../services/columnWidthCalculator';

/**
 * Mixin for managing column resize operations in a virtual table.
 * Each table instance gets its own isolated resize state.
 */
export default {
    data() {
        return {
            isResizing: false,
            resizingColumn: null,
            recentlyResizedColumn: null,
            manuallyResizedColumns: new Set(),
            // Internal state
            _currentConfig: null,
            _onWidthChange: null
        };
    },

    methods: {
        // Handles mouse movement during resize
        _handleMouseMove(event) {
            if (!this.isResizing || !this._currentConfig) return;

            const deltaX = event.clientX - this._currentConfig.startX;
            if (Math.abs(deltaX) < 1) return;

            let newWidths = this._calculateNewWidths(deltaX, this._currentConfig);
            
            // Calculate total width and check if it exceeds container
            const totalWidth = Object.values(newWidths).reduce((sum, w) => sum + w, 0);
            const containerWidth = this._currentConfig.containerWidth;
            
            // If total exceeds container, proportionally reduce all columns EXCEPT actions
            if (totalWidth > containerWidth) {
                const excess = totalWidth - containerWidth;
                const columnKeys = Object.keys(newWidths);
                const nonActionsKeys = columnKeys.filter(k => k !== 'actions');
                const nonActionsTotalWidth = nonActionsKeys.reduce((sum, k) => sum + newWidths[k], 0);
                
                // Reduce non-actions columns proportionally
                nonActionsKeys.forEach(key => {
                    const proportion = newWidths[key] / nonActionsTotalWidth;
                    const reduction = excess * proportion;
                    newWidths[key] = Math.max(MIN_WIDTH, newWidths[key] - reduction);
                });
                
                // Ensure actions stays at 55px minimum
                if (newWidths.actions) {
                    newWidths.actions = Math.max(ACTIONS_WIDTH, newWidths.actions);
                }
            }
            
            if (this._onWidthChange) {
                this._onWidthChange(newWidths);
            }
        },

        // Calculates new column widths based on mouse movement
        _calculateNewWidths(deltaX, config) {
            const { columnKey, startWidths } = config;
            const columnKeys = Object.keys(startWidths);
            const growingColumn = this._getGrowingColumn(columnKeys);
            const isResizingGrowingColumn = columnKey === growingColumn;

            // Simple case: resizing the growing column itself
            if (isResizingGrowingColumn) {
                return this._resizeGrowingColumn(columnKey, deltaX, startWidths);
            }

            // Complex case: resizing a non-growing column
            return this._resizeNonGrowingColumn(
                columnKey,
                deltaX,
                columnKeys,
                growingColumn,
                startWidths,
                config.containerWidth
            );
        },

        // Handles resizing when the user is dragging the growing column
        _resizeGrowingColumn(columnKey, deltaX, startWidths) {
            const newWidths = { ...startWidths };
            const startWidth = startWidths[columnKey];
            const minWidth = columnKey === 'actions' ? ACTIONS_WIDTH : MIN_WIDTH;
            const calculatedWidth = startWidth + deltaX;
            const finalWidth = Math.max(minWidth, calculatedWidth);
            newWidths[columnKey] = finalWidth;
            return newWidths;
        },

        // Handles resizing when the user is dragging a non-growing column
        _resizeNonGrowingColumn(columnKey, deltaX, columnKeys, growingColumn, startWidths, containerWidth) {
            const newWidths = { ...startWidths };
            const startWidth = startWidths[columnKey];
            const minWidth = columnKey === 'actions' ? ACTIONS_WIDTH : MIN_WIDTH;
            const calculatedWidth = startWidth + deltaX;
            const newWidth = Math.max(minWidth, calculatedWidth);
            const widthChange = newWidth - startWidth;

            // When shrinking, the growing column expands automatically via CSS
            if (widthChange < 0) {
                newWidths[columnKey] = newWidth;
                return newWidths;
            }

            // When growing, take space strategically (but never from actions)
            const actualNewWidth = this._calculateGrowingColumnWidth(
                columnKey,
                widthChange,
                columnKeys,
                growingColumn,
                startWidths,
                containerWidth,
                newWidths
            );

            newWidths[columnKey] = actualNewWidth;
            return newWidths;
        },

        // Calculates width when growing a column by taking space from other columns
        _calculateGrowingColumnWidth(
            columnKey, widthChange, columnKeys, growingColumn, startWidths, containerWidth, newWidths) {
            const startWidth = startWidths[columnKey];
            const growingColumnActualWidth = this._getGrowingColumnActualWidth(
                columnKeys,
                growingColumn,
                startWidths,
                containerWidth
            );
            const growingMinWidth = growingColumn === 'actions' ? ACTIONS_WIDTH : MIN_WIDTH;
            const availableFromGrowingColumn = Math.max(0, growingColumnActualWidth - growingMinWidth);

            let actualNewWidth = startWidth;
            let remainingChange = widthChange;

            // Take from growing column first
            const fromGrowingColumn = Math.min(remainingChange, availableFromGrowingColumn);
            actualNewWidth += fromGrowingColumn;
            remainingChange -= fromGrowingColumn;

            // If more space needed, take from other columns proportionally
            // BUT never from the actions column
            if (remainingChange > 0) {
                const otherColumns = columnKeys.filter((key) => 
                    key !== columnKey && 
                    key !== growingColumn && 
                    key !== 'actions' // Never take space from actions
                );
                const availableFromOthers = this._calculateAvailableSpace(otherColumns, startWidths);
                const fromOthers = Math.min(remainingChange, availableFromOthers);

                actualNewWidth += fromOthers;
                this._distributeWidthChange(otherColumns, fromOthers, startWidths, newWidths);
            }

            return actualNewWidth;
        },

        // Gets the actual rendered width of the growing column
        _getGrowingColumnActualWidth(columnKeys, growingColumn, startWidths, containerWidth) {
            const nonGrowingColumnsWidth = columnKeys
                .filter((key) => key !== growingColumn)
                .reduce((sum, key) => sum + startWidths[key], 0);

            const calculatedWidth = containerWidth - nonGrowingColumnsWidth;
            
            // If calculated width would be negative, we've exceeded container
            // Return the minimum allowed width for this column
            if (calculatedWidth < 0) {
                const minWidth = growingColumn === 'actions' ? ACTIONS_WIDTH : MIN_WIDTH;
                return minWidth;
            }
            
            return calculatedWidth;
        },

        // Determines which column grows to fill available space
        _getGrowingColumn(columnKeys) {
            // The growing column is the last column, or second-to-last if actions column exists
            const hasActions = columnKeys.includes('actions');
            const growingColumnIndex = hasActions ? columnKeys.indexOf('actions') - 1 : columnKeys.length - 1;
            return columnKeys[growingColumnIndex];
        },

        // Calculates total space available for shrinking from specified columns
        _calculateAvailableSpace(columnKeys, widths) {
            return columnKeys.reduce((sum, key) => {
                const minWidth = key === 'actions' ? ACTIONS_WIDTH : MIN_WIDTH;
                return sum + Math.max(0, widths[key] - minWidth);
            }, 0);
        },

        // Distributes width change proportionally across target columns
        _distributeWidthChange(targetColumns, widthToDistribute, startWidths, newWidths) {
            if (targetColumns.length === 0 || widthToDistribute === 0) return;

            const totalWidth = targetColumns.reduce((sum, key) => sum + startWidths[key], 0);

            targetColumns.forEach((key) => {
                const minWidth = key === 'actions' ? ACTIONS_WIDTH : MIN_WIDTH;
                const proportion = startWidths[key] / totalWidth;
                const adjustment = widthToDistribute * proportion;
                newWidths[key] = Math.max(minWidth, startWidths[key] - adjustment);
            });
        },

        // Finishes the resize operation
        _handleMouseUp() {
            const wasResizing = this.isResizing;
            const columnBeingResized = this.resizingColumn;

            this.isResizing = false;
            this.resizingColumn = null;
            this._currentConfig = null;
            this._onWidthChange = null;

            this._detachEventListeners();
            this._clearResizeCursor();

            // Mark column as recently resized to prevent sorting clicks
            if (wasResizing && columnBeingResized) {
                this._markColumnAsResized(columnBeingResized);
            }
        },

        // Marks a column as recently resized and tracks it for future recalculations
        _markColumnAsResized(columnKey) {
            this.recentlyResizedColumn = columnKey;
            this.manuallyResizedColumns.add(columnKey);

            // Clear the recently resized flag on the next frame to prevent accidental sort clicks
            this.$nextTick(() => {
                if (this.recentlyResizedColumn === columnKey) {
                    this.recentlyResizedColumn = null;
                }
            });
        },

        _attachEventListeners() {
            document.addEventListener('mousemove', this._handleMouseMove);
            document.addEventListener('mouseup', this._handleMouseUp);
        },

        _detachEventListeners() {
            document.removeEventListener('mousemove', this._handleMouseMove);
            document.removeEventListener('mouseup', this._handleMouseUp);
        },

        _setResizeCursor() {
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },

        _clearResizeCursor() {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        },

        // Starts a resize operation for the given column
        startColumnResize(column, event, currentWidths, containerWidth, widthChangeCallback) {
            if (column === 'actions') return;

            this.isResizing = true;
            this.resizingColumn = column;
            this._onWidthChange = widthChangeCallback;

            this._currentConfig = {
                columnKey: column,
                startX: event.clientX,
                startWidths: { ...currentWidths },
                containerWidth
            };

            this._attachEventListeners();
            this._setResizeCursor();
        },

        // Cleans up any active resize operation
        cleanupColumnResize() {
            if (this.isResizing) {
                this._handleMouseUp();
            }
        }
    },

    beforeDestroy() {
        this.cleanupColumnResize();
    }
};
